import React from "react";

import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  Link as RouterLink,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router";

import { useOrgSession } from "src/domains/auth/hooks/useOrgSession";
import { useLegacyAuth } from "src/domains/auth/module/components/AuthProvider";
import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { AUTH, ORGANIZATIONS, ROOT } from "src/platform/constants/routes";

interface ProtectedRouteProps {
  children?: React.ReactNode;
  redirectPath?: string;
}

/**
 * A component that ensures a user is authenticated before allowing access to a route.
 * Authentication is cookie-based (httpOnly session cookie validated via /auth/me),
 * so this checks for an existing user rather than a JS-accessible token.
 * If not authenticated, it redirects to the login page with the attempted location.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectPath = AUTH.LOGIN.path,
}) => {
  const { user, loading } = useLegacyAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        component="main"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress aria-label="Checking authentication" />
        <Typography variant="h6" component="h1" sx={{ mt: 2 }}>
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  if (!user) {
    // Redirect to login page with return URL
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // If there are children, render them, otherwise render the outlet
  return children ? <>{children}</> : <Outlet />;
};

interface OrgScopedRouteProps {
  children?: React.ReactNode;
}

/**
 * Establishes the organization-scoped session required by routes containing
 * an organization alias before rendering their data-loading children.
 *
 * The session JWT lives in an httpOnly cookie. The in-memory org context is
 * tracked by `orgSessionManager`. If there is no org context at all (the user
 * landed on a workspace route without selecting an org), redirect to login so
 * they can establish a step-2 session.
 */
export const OrgScopedRoute: React.FC<OrgScopedRouteProps> = ({ children }) => {
  const { orgAlias: routeOrgAlias } = useParams<{ orgAlias: string }>();
  const { switching, orgAlias, selectOrg } = useOrgSession();
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    if (!routeOrgAlias) {
      setReady(false);
      return () => {
        cancelled = true;
      };
    }

    // If there is no org context at all, the selectOrg call below will attempt
    // to establish the org session from the route alias. If that fails (user is
    // not authenticated or not a member), an error is surfaced below.

    if (orgAlias === routeOrgAlias) {
      setError(null);
      setReady(true);
      return () => {
        cancelled = true;
      };
    }

    setReady(false);
    setError(null);
    void selectOrg(routeOrgAlias)
      .then(() => {
        if (!cancelled) setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setReady(false);
          setError("We could not select this organization.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orgAlias, routeOrgAlias, selectOrg]);

  // No org context at all and route alias mismatch — send the user back to
  // login so they can establish an org-scoped session.
  if (!routeOrgAlias && !orgSessionManager.getOrgAlias()) {
    return <Navigate to={AUTH.LOGIN.path} replace />;
  }

  if (error) {
    // If the failure was due to no existing org context, redirect to login
    // so the user can re-establish an org-scoped session.
    if (!orgSessionManager.getOrgAlias()) {
      return <Navigate to={AUTH.LOGIN.path} replace />;
    }
    return (
      <Box
        role="alert"
        sx={{
          display: "grid",
          gap: 1.5,
          placeItems: "start",
          p: 3,
        }}
      >
        <Typography variant="h6">Organization session unavailable</Typography>
        <Typography color="text.secondary">
          {error} Return to Organizations and choose an organization you can
          access before opening this project link.
        </Typography>
        <Button
          component={RouterLink}
          to={ORGANIZATIONS.ROOT.path}
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          Return to Organizations
        </Button>
      </Box>
    );
  }

  if (!ready || switching) {
    return (
      <Box
        role="status"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress aria-label="Selecting organization" />
      </Box>
    );
  }

  return children ? <>{children}</> : <Outlet />;
};

interface PublicRouteProps {
  children?: React.ReactNode;
  restricted?: boolean;
  redirectPath?: string;
}

/**
 * A component for public routes that may optionally restrict authenticated users
 * If restricted is true and the user is authenticated, they are redirected to the home page
 * Special case: Don't redirect away from login page even if user has a token - let login page handle organization selection
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  restricted = false,
  redirectPath = ROOT.path,
}) => {
  const { user, loading } = useLegacyAuth();
  const location = useLocation();
  const from = location.state?.from || redirectPath;
  const isLoginPage = location.pathname === AUTH.LOGIN.path;

  if (loading) {
    return (
      <Box
        component="main"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress aria-label="Checking authentication" />
        <Typography variant="h6" component="h1" sx={{ mt: 2 }}>
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  if (restricted && user && !isLoginPage) {
    // Redirect to the intended location or specified redirect path
    return <Navigate to={from} replace />;
  }

  // If there are children, render them, otherwise render the outlet
  return children ? <>{children}</> : <Outlet />;
};
